import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Smartphone,
  MapPin,
  Clock,
  CheckCircle,
  AlertTriangle,
  Users,
  Radio,
  Camera,
  Send,
  Navigation,
  Phone,
  MessageCircle,
  FileText,
  ArrowLeft,
  ArrowRight,
  Target,
  Heart,
  Droplets,
  Flame,
  Building2,
  Wifi,
  Battery,
  Signal
} from 'lucide-react';

interface Task {
  id: string;
  type: 'rescue' | 'medical' | 'fire' | 'evacuation' | 'supply';
  location: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  status: 'assigned' | 'in-progress' | 'completed' | 'failed';
  assignedAt: string;
  peopleAffected?: number;
  resources?: string[];
}

interface Responder {
  id: string;
  name: string;
  role: string;
  team: string;
  status: 'online' | 'responding' | 'offline';
  location: string;
  battery: number;
  signal: number;
  currentTask?: Task;
}

const FieldResponderView: React.FC = () => {
  const [responder] = useState<Responder>({
    id: 'resp1',
    name: 'Lt. Rodriguez',
    role: 'Rescue Specialist',
    team: 'Alpha Team',
    status: 'responding',
    location: 'Zone A, Sector 7',
    battery: 78,
    signal: 92
  });

  const [tasks, setTasks] = useState<Task[]>([
    {
      id: '1',
      type: 'rescue',
      location: 'Zone A, Building #23, 3rd Floor',
      description: '4 people trapped on rooftop, including 1 child. Need immediate extraction.',
      priority: 'high',
      status: 'in-progress',
      assignedAt: '10:47 AM',
      peopleAffected: 4,
      resources: ['Boat', 'Harness', 'First Aid']
    },
    {
      id: '2',
      type: 'medical',
      location: 'Zone A, Emergency Shelter',
      description: 'Medical emergency - Heart attack patient needs immediate attention',
      priority: 'high',
      status: 'assigned',
      assignedAt: '10:52 AM',
      peopleAffected: 1,
      resources: ['AED', 'Oxygen', 'Stretcher']
    },
    {
      id: '3',
      type: 'supply',
      location: 'Zone A, Distribution Point',
      description: 'Deliver medical supplies to forward operating base',
      priority: 'medium',
      status: 'assigned',
      assignedAt: '10:55 AM',
      resources: ['Medical Kits', 'Water', 'Food']
    }
  ]);

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [checkInMessage, setCheckInMessage] = useState('');

  const getTaskIcon = (type: string) => {
    switch(type) {
      case 'rescue': return <Users size={16} className="text-cyan-500" />;
      case 'medical': return <Heart size={16} className="text-red-500" />;
      case 'fire': return <Flame size={16} className="text-orange-500" />;
      case 'evacuation': return <Navigation size={16} className="text-yellow-500" />;
      case 'supply': return <FileText size={16} className="text-green-500" />;
      default: return <AlertTriangle size={16} className="text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'high': return 'text-red-500 border-red-500 bg-red-500/10';
      case 'medium': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-green-500 border-green-500 bg-green-500/10';
      default: return 'text-gray-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'in-progress': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      case 'assigned': return 'text-blue-500 border-blue-500 bg-blue-500/10';
      case 'completed': return 'text-green-500 border-green-500 bg-green-500/10';
      case 'failed': return 'text-red-500 border-red-500 bg-red-500/10';
      default: return 'text-gray-500';
    }
  };

  const updateTaskStatus = (taskId: string, newStatus: Task['status']) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, status: newStatus } : task
    ));
    setSelectedTask(null);
  };

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 holographic-card h-[600px] flex flex-col">
      {/* Header - Responder Info */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Smartphone className="text-green-500 animate-pulse" />
            FIELD RESPONDER VIEW
          </h2>
          <p className="text-xs text-gray-400 mt-1">Mobile interface simulation</p>
        </div>

        {/* Device Status */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Signal size={14} className={responder.signal > 80 ? 'text-green-500' : 'text-yellow-500'} />
            <span className="text-xs">{responder.signal}%</span>
          </div>
          <div className="flex items-center gap-1">
            <Battery size={14} className={responder.battery > 50 ? 'text-green-500' : 'text-yellow-500'} />
            <span className="text-xs">{responder.battery}%</span>
          </div>
          <div className={`px-2 py-1 rounded-lg text-xs ${
            responder.status === 'online' ? 'bg-green-500/20 text-green-500' :
            responder.status === 'responding' ? 'bg-yellow-500/20 text-yellow-500 animate-pulse' :
            'bg-gray-500/20 text-gray-500'
          }`}>
            {responder.status.toUpperCase()}
          </div>
        </div>
      </div>

      {/* Responder Info Card */}
      <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 rounded-lg p-4 mb-6 border border-cyan-500/30">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-600 to-blue-600 rounded-full flex items-center justify-center text-xl font-bold">
            {responder.name.charAt(0)}
          </div>
          <div>
            <h3 className="font-bold">{responder.name}</h3>
            <p className="text-sm text-gray-400">{responder.role} • {responder.team}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <MapPin size={14} className="text-cyan-500" />
          {responder.location}
        </div>
      </div>

      {/* Current Task (if any) */}
      {tasks.find(t => t.status === 'in-progress') && (
        <div className="mb-6">
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <Target size={14} className="text-red-500 animate-pulse" />
            CURRENT MISSION
          </h3>
          {tasks.filter(t => t.status === 'in-progress').map(task => (
            <motion.div
              key={task.id}
              layoutId={task.id}
              className="bg-red-500/10 border border-red-500/30 rounded-lg p-4"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  {getTaskIcon(task.type)}
                  <span className="font-bold capitalize">{task.type} Mission</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(task.priority)}`}>
                  {task.priority}
                </span>
              </div>
              <p className="text-sm mb-2">{task.description}</p>
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                <MapPin size={10} />
                {task.location}
                <Clock size={10} className="ml-2" />
                {task.assignedAt}
              </div>
              {task.peopleAffected && (
                <div className="flex items-center gap-1 text-xs text-cyan-500 mb-2">
                  <Users size={12} />
                  {task.peopleAffected} people affected
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <button className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-bold py-2 px-3 rounded-lg">
                  COMPLETE
                </button>
                <button className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2 px-3 rounded-lg">
                  NEED HELP
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Assigned Tasks List */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scroll">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <Radio size={14} className="text-cyan-500" />
          ASSIGNED TASKS
        </h3>

        {tasks.map((task) => (
          <motion.div
            key={task.id}
            layout
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-black/30 rounded-lg p-4 border border-gray-700 hover:border-cyan-500/50 cursor-pointer transition-all"
            onClick={() => setSelectedTask(task)}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                {getTaskIcon(task.type)}
                <span className="font-bold text-sm capitalize">{task.type}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(task.status)}`}>
                  {task.status}
                </span>
              </div>
            </div>

            <p className="text-xs text-gray-300 mb-2 line-clamp-2">{task.description}</p>

            <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
              <MapPin size={10} />
              {task.location}
              <Clock size={10} className="ml-2" />
              {task.assignedAt}
            </div>

            <div className={`text-xs px-2 py-1 inline-block rounded ${getPriorityColor(task.priority)}`}>
              {task.priority.toUpperCase()} PRIORITY
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mt-4 grid grid-cols-4 gap-2">
        <button className="p-3 bg-cyan-600/20 hover:bg-cyan-600/30 rounded-lg flex flex-col items-center gap-1">
          <Camera size={20} className="text-cyan-500" />
          <span className="text-[10px]">Camera</span>
        </button>
        <button className="p-3 bg-green-600/20 hover:bg-green-600/30 rounded-lg flex flex-col items-center gap-1">
          <MessageCircle size={20} className="text-green-500" />
          <span className="text-[10px]">Chat</span>
        </button>
        <button className="p-3 bg-red-600/20 hover:bg-red-600/30 rounded-lg flex flex-col items-center gap-1">
          <AlertTriangle size={20} className="text-red-500" />
          <span className="text-[10px]">SOS</span>
        </button>
        <button className="p-3 bg-purple-600/20 hover:bg-purple-600/30 rounded-lg flex flex-col items-center gap-1">
          <Navigation size={20} className="text-purple-500" />
          <span className="text-[10px]">Navigate</span>
        </button>
      </div>

      {/* Task Detail Modal */}
      <AnimatePresence>
        {selectedTask && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setSelectedTask(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-gray-900 w-96 p-6 rounded-xl border border-cyan-500/50"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                {getTaskIcon(selectedTask.type)}
                <span className="capitalize">{selectedTask.type} Task</span>
              </h3>

              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Description</p>
                  <p className="text-sm">{selectedTask.description}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-400 mb-1">Location</p>
                  <div className="flex items-center gap-1 text-sm">
                    <MapPin size={14} className="text-cyan-500" />
                    {selectedTask.location}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400 mb-1">Assigned At</p>
                  <div className="flex items-center gap-1 text-sm">
                    <Clock size={14} className="text-cyan-500" />
                    {selectedTask.assignedAt}
                  </div>
                </div>

                {selectedTask.peopleAffected && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">People Affected</p>
                    <div className="flex items-center gap-1 text-sm">
                      <Users size={14} className="text-cyan-500" />
                      {selectedTask.peopleAffected}
                    </div>
                  </div>
                )}

                {selectedTask.resources && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Resources</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedTask.resources.map((res, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-cyan-600/20 rounded">
                          {res}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  {selectedTask.status === 'assigned' && (
                    <>
                      <button
                        onClick={() => updateTaskStatus(selectedTask.id, 'in-progress')}
                        className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 rounded-lg"
                      >
                        START MISSION
                      </button>
                      <button className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 rounded-lg">
                        CANCEL
                      </button>
                    </>
                  )}

                  {selectedTask.status === 'in-progress' && (
                    <>
                      <button
                        onClick={() => updateTaskStatus(selectedTask.id, 'completed')}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg"
                      >
                        COMPLETE
                      </button>
                      <button className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg">
                        NEED HELP
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Check-in Modal */}
      <AnimatePresence>
        {showCheckIn && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setShowCheckIn(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-gray-900 w-96 p-6 rounded-xl border border-green-500/50"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-green-500 mb-4">CHECK-IN</h3>
              <textarea
                value={checkInMessage}
                onChange={(e) => setCheckInMessage(e.target.value)}
                placeholder="Update your status..."
                rows={3}
                className="w-full bg-black/50 border border-gray-700 rounded-lg px-3 py-2 text-sm mb-4"
              />
              <div className="flex gap-3">
                <button className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg">
                  SEND UPDATE
                </button>
                <button
                  onClick={() => setShowCheckIn(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 rounded-lg"
                >
                  CANCEL
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FieldResponderView;